import React, { useState, useEffect, useRef } from "react";

import Button from "react-bootstrap/Button";

import * as api from "../../../../api";
import generateRandomToken from "../../../../utils/generateRandomToken";
import RfidModal from "../../../../components/RfidModal/RfidModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import CashOrCreditModal from "../../../../components/CashOrCreditModal/CashOrCreditModal";
import GlobalScan from "../../../../components/GlobalScan/globalScan";
import CustomToast from "../../../../components/Toast/toast";
import CustomerModal from "../../../../components/CustomerModal/CustomerModal";

import "./TopUpOrCashOut.sass";

const buttonAmounts = [5, 10, 20, 50, 100, 200, 500, 1000];

export default function TopUpOrCashOut({ type = "topUp", currentUser }) {
  const [amount, setAmount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [token, setToken] = useState(generateRandomToken());
  const [successCustomer, setSuccessCustomer] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [compCash, setCompCash] = useState(false);
  const [method, setMethod] = useState("");
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [globalScanDisabled, setGlobalScanDisabled] = useState(false);
  const [customer, setCustomer] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const inputRef = useRef(null);
  const submitRef = useRef(null);
  const resetRef = useRef(null);

  useEffect(() => {
    if (showMethodModal || showModal) {
      setGlobalScanDisabled(true);
    } else {
      setGlobalScanDisabled(false);
    }
  }, [showMethodModal, showModal]);

  useEffect(() => {
    if (Object.keys(customer)?.length > 0) {
      setShowToast(true);
    } else {
      setShowToast(false);
    }
  }, [customer]);

  useEffect(() => {
    if (!showCustomerModal && !showToast) {
      setCustomer({});
      setTransactions([]);
    }
  }, [showToast, showCustomerModal]);

  const handleSubmit = async (rfid) => {
    setErrorMessage(null);
    let body = { amount: amount * 100, rfid, token };
    if (type === "topUp" && compCash) {
      body.isNcCash = true;
      body.transactionMethod = "system";
    } else if (type === "topUp" && !compCash) {
      body.transactionMethod = method;
    }
    const customer = await api.cashTransactions[type](body).catch((error) => {
      const data = error.response.data;
      const dataKeys = Object.keys(data);
      setErrorMessage(data[dataKeys[0]][0]);
    });
    setSuccessCustomer(customer);

    if (customer) {
      setAmount(0);
      setCompCash(false);
      setMethod("");
      setErrorMessage(null);
    }

    setToken(generateRandomToken());
  };

  const handleHide = () => {
    setShowModal(false);
  };
  const handleExited = () => {
    setSuccessCustomer(false);
    setErrorMessage(null);
  };

  const onButtonClick = (addAmount) => {
    setAmount(amount + addAmount);
  };
  const handleChange = (value) => {
    value = parseInt(value, 10);

    if (isNaN(value)) setAmount(0);
    else setAmount(value);
  };

  const handleInputSubmit = () => {
    if (type === "topUp" && !compCash) {
      setShowMethodModal(true);
    } else {
      setShowModal(true);
    }
  };

  const handleMethodChoice = (choice) => {
    setMethod(choice);
    setShowModal(true);
    setShowMethodModal(false);
  };

  const fetchCustomerTransactions = () => {
    api.cashTransactions
      .byCustomer(customer.pk)
      .then((response) => setTransactions(response.results));
  };

  const isTopUp = type === "topUp";

  return (
    <>
      <div className={`TopUpOrCashOut TopUpOrCashOut--${type}`}>
        <div className="TopUpOrCashOut__grid">
          <section
            className="TopUpOrCashOut__presets"
            aria-labelledby="topup-presets-heading"
          >
            <h2
              id="topup-presets-heading"
              className="TopUpOrCashOut__section-title"
            >
              Quick amounts
            </h2>
            <div className="TopUpOrCashOut__preset-grid">
              {buttonAmounts.map((amt) => (
                <Button
                  key={amt}
                  type="button"
                  variant={isTopUp ? "outline-success" : "outline-danger"}
                  className="TopUpOrCashOut__preset-btn"
                  onClick={() => onButtonClick(amt)}
                >
                  +{amt}
                </Button>
              ))}
            </div>
          </section>

          <aside className="TopUpOrCashOut__panel">
            <h2 className="TopUpOrCashOut__section-title">Amount</h2>
            <div className="TopUpOrCashOut__display">
              <div className="TopUpOrCashOut__amount-input-wrap">
                <input
                  className="TopUpOrCashOut__amount-input form-control border-0 shadow-none"
                  type="number"
                  name="amount"
                  id="amount"
                  value={amount}
                  onChange={(event) => handleChange(event.target.value)}
                  ref={inputRef}
                  onFocus={() => setGlobalScanDisabled(true)}
                  onBlur={() => setGlobalScanDisabled(false)}
                  inputMode="numeric"
                  aria-label="Amount in EGP"
                />
                <span className="TopUpOrCashOut__currency">EGP</span>
              </div>
              <Button
                variant="outline-secondary"
                className="TopUpOrCashOut__reset"
                onClick={() => setAmount(0)}
                ref={resetRef}
                onFocus={() => resetRef?.current?.blur()}
              >
                Reset
              </Button>
            </div>

            <Button
              disabled={!amount}
              variant="primary"
              className="TopUpOrCashOut__proceed"
              onClick={() => handleInputSubmit()}
              ref={submitRef}
              onFocus={() => submitRef?.current?.blur()}
            >
              Proceed
            </Button>

            {currentUser?.canAddNcCash && isTopUp && (
              <div className="comp-cash-div">
                <button
                  type="button"
                  className={
                    "comp-cash-btn" + (compCash ? " comp-cash-btn--active" : "")
                  }
                  onClick={() => setCompCash(!compCash)}
                  aria-pressed={compCash}
                  aria-label="Comp cash"
                >
                  {compCash && (
                    <FontAwesomeIcon icon={faCheck} className="check-icon" />
                  )}
                </button>
                <button
                  type="button"
                  className="comp-cash-label"
                  onClick={() => setCompCash(!compCash)}
                >
                  Comp cash
                </button>
              </div>
            )}
          </aside>
        </div>
      </div>

      <RfidModal
        show={showModal}
        onHide={handleHide}
        onExited={handleExited}
        onSubmit={handleSubmit}
        successMessage={
          (isTopUp ? "Top up" : "Cash out") + " successful!"
        }
        successCustomer={successCustomer}
        errorMessage={errorMessage}
        title="Scan Customer RFID"
        showNfcButton={true}
      />
      <CashOrCreditModal
        show={showMethodModal}
        onHide={() => setShowMethodModal(false)}
        handleChoice={handleMethodChoice}
      />
      <GlobalScan
        onScan={(value) => setCustomer(value)}
        globalScanDisabled={globalScanDisabled}
        setGlobalScanDisabled={setGlobalScanDisabled}
      />
      <CustomToast
        show={showToast}
        onClose={() => setShowToast(false)}
        body={`Balance: ${customer?.balance / 100} EGP`}
        header={customer?.firstName + " " + customer?.lastName}
        handleToastClick={() => setShowCustomerModal(true)}
        timeout={10}
      />
      <CustomerModal
        show={showCustomerModal}
        onHide={() => {
          setShowToast(false);
          setShowCustomerModal(false);
          setCustomer({});
          setTransactions([]);
        }}
        onExited={() => {
          setShowToast(false);
          setShowCustomerModal(false);
          setCustomer({});
          setTransactions([]);
        }}
        onShow={fetchCustomerTransactions}
        customer={customer}
        cashTransactions={transactions}
      />
    </>
  );
}
